import React from 'react';
import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

/**
 * Strokes to Par badge — USGA-compliant Personal Recreational Value (PRV).
 *
 * PRV COMPLIANCE (USGA Golfer Product Access, Sep 2023):
 *   - Whole number only (never decimal — avoids Handicap Index confusion)
 *   - Never labeled with "handicap", "index", or WHS terminology
 *   - Computed from par values only (no Course Rating / Slope Rating)
 *   - Not used in net-score competition inside the app
 *
 * Post-GPA (v1.1): a separate <HandicapIndexBadge> will show the user's real
 * Handicap Index® imported via GHIN API, with proper trademark treatment.
 *
 * Props:
 *   display_metric: { value: number|null, source: 'strokes_to_par'|'none', label: string|null }
 *   strokes_to_par: optional { value, rounds_used, rounds_total, method, recent_diffs }
 *   size: 'sm' | 'md' (default 'md')
 *   showLabel: boolean — include "Strokes to Par" prefix (default true)
 *   showInfo: boolean — show the info "?" affordance (default true)
 */
export const StrokesToParBadge = ({
  display_metric,
  strokes_to_par,
  size = 'md',
  showLabel = true,
  showInfo = true,
}) => {
  if (!display_metric || display_metric.value == null) return null;
  const sizeCls = size === 'sm'
    ? 'px-2 py-0.5 text-[10px]'
    : 'px-3 py-1.5 text-xs';
  const colorCls = 'bg-primary/10 text-primary border border-primary/20';
  // Whole number — PRV compliance requirement
  const valueStr = String(Math.round(Number(display_metric.value)));
  const prefix = display_metric.value > 0 ? '+' : '';

  const badge = (
    <span
      data-testid={`strokes-to-par-badge-${display_metric.source}`}
      className={`inline-flex items-center gap-1 rounded-full font-bold ${sizeCls} ${colorCls}`}
      title="Strokes to Par — personal tracking metric"
    >
      {showLabel && <span className="opacity-70">{display_metric.label}</span>}
      <span>{prefix}{valueStr}</span>
    </span>
  );

  if (!showInfo) return badge;

  return (
    <span className="inline-flex items-center gap-1">
      {badge}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Strokes to Par details"
            data-testid={`strokes-to-par-info-${display_metric.source}`}
            className="inline-flex items-center justify-center w-4 h-4 rounded-full text-muted-foreground hover:text-foreground transition-colors"
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-80 text-xs leading-relaxed"
          data-testid="strokes-to-par-popover"
        >
          <div className="space-y-2">
            <p className="font-bold text-sm">Strokes to Par — {prefix}{valueStr}</p>
            <p className="text-muted-foreground">
              A personal tracking metric showing your typical score relative to par, based on 18-hole rounds posted in The Club.
            </p>

            {strokes_to_par?.rounds_used != null && (
              <div className="text-muted-foreground">
                <span className="font-semibold text-foreground">Rounds used:</span>{' '}
                {strokes_to_par.rounds_used} of {strokes_to_par.rounds_total} (best rounds averaged)
              </div>
            )}

            {Array.isArray(strokes_to_par?.recent_diffs) && strokes_to_par.recent_diffs.length > 0 && (
              <div>
                <p className="font-semibold text-foreground mb-1">Best recent rounds (strokes vs par):</p>
                <div className="flex flex-wrap gap-1">
                  {strokes_to_par.recent_diffs.map((d, i) => (
                    <span key={`stp-${i}-${d}`} className="px-1.5 py-0.5 rounded bg-primary/10 text-primary font-mono text-[10px]">
                      {d > 0 ? '+' : ''}{d}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-2 border-t border-border space-y-1">
              <p className="font-semibold text-foreground">How it&apos;s calculated</p>
              <p className="text-muted-foreground">
                Your score minus par for each round, then we average your best rounds. Simple and honest — no adjustments for course difficulty.
              </p>
            </div>

            <p className="text-[10px] text-muted-foreground italic pt-2 border-t border-border">
              Strokes to Par is a personal recreational metric, not a golf handicap. It cannot be used for equitable competition. For an official Handicap Index, post scores through your USGA-affiliated club.
            </p>
          </div>
        </PopoverContent>
      </Popover>
    </span>
  );
};

export default StrokesToParBadge;
